import { useContext } from 'react'
import type { PropsWithChildren, HTMLAttributes } from 'react'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import { ModalContext } from '../../provider/ModalProvider'

import { MODALS_ID } from '../../constants'
import type { ModalsIds } from '../../constants/modal'

import { Color } from '../../theme/color'

interface ModalProps extends PropsWithChildren<Omit<HTMLAttributes<HTMLDivElement>, 'id'>> {
  id: ModalsIds
  backdrop?: boolean
  onClose?: () => void
}

export const Modal = Object.assign(
  observer(({ children, id, onClose, backdrop = true, ...props }: ModalProps) => {
    const { modals, closeModal } = useContext(ModalContext)

    const modalFindIndex = modals.findIndex((modal) => modal.id === id)

    const backdropClose = () => {
      closeModal(MODALS_ID[id])
      onClose?.()
    }

    return (
      <Wrap>
        {backdrop && <Backdrop isShow={modalFindIndex > -1} onClick={backdropClose} />}
        <Container {...props} isShow={modalFindIndex > -1}>
          {children}
        </Container>
      </Wrap>
    )
  }),
  { Footer: ModalFooter },
)

function ModalFooter({ children }: { children: any }) {
  return <Footer>{children}</Footer>
}

const Wrap = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 21000;
  width: 100%;
  height: 100%;
  max-width: 500px;
`

const Backdrop = styled.div<{ isShow: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  ${({ isShow }) => (isShow ? 'z-index: 21001;' : '')}
  width: 100%;
  height: 100%;
  background-color: rgba(28, 34, 39, 0.6);
`

const Container = styled.div<{ isShow: boolean }>`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: absolute;
  top: 50%;
  left: 50%;
  ${({ isShow }) => (isShow ? 'z-index: 21002;' : '')}
  width: calc(100% - 64px);
  min-width: 290px;
  max-width: 330px;
  max-height: calc(100% - 280px);
  padding: 24px 16px 16px;
  border-radius: 8px;
  background-color: ${Color.white};
  transform: translate(-50%, -50%);
  > * {
    flex-shrink: 0;
  }
`

const Footer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 24px;
  > * {
    flex: 1;
  }
`
